import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useJsApiLoader } from '@react-google-maps/api';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid, MenuItem, CircularProgress } from '@mui/material';
import { getActivePractices } from '../services/patientService';

// Gender options will be translated in component
const maritalStatuses = ["Single", "Married", "Divorce/Widowed"];
const states = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

// Move libraries outside component to prevent reloading
const GOOGLE_MAPS_LIBRARIES = ['places'];

function PharmacyAutocomplete({ value, onSelect, error, helperText }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!isLoaded || !inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    
    // Try the new AutocompleteSuggestion API first, but it's not widely available yet
    // For now, let's use the reliable old API
    fallbackToOldAPI();

    function fallbackToOldAPI() {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions({
        input: inputValue,
        types: ['pharmacy'],
        componentRestrictions: { country: 'us' },
        fields: ['name', 'formatted_address'],
      }, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setOptions(predictions.map(prediction => ({
            label: prediction.description,
            value: prediction.description,
            placeId: prediction.place_id
          })));
        } else {
          setOptions([]);
        }
        setLoading(false);
      });
    }
  }, [inputValue, isLoaded]);

  return (
    <Autocomplete
      freeSolo
      clearOnBlur={false}
      filterOptions={(x) => x}  // Disable client-side filtering
      options={options}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
      value={value || ''}
      inputValue={inputValue}
      loading={loading}
      onInputChange={(e, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'clear') {
          onSelect('');
        }
      }}
      onChange={(e, newValue) => {
        const value = typeof newValue === 'string' ? newValue : (newValue?.value || '');
        onSelect(value);
        setInputValue(value);
      }}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label="Start typing pharmacy name..." 
          fullWidth
          required
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={inputValue && inputValue.length >= 2 ? "No pharmacies found" : "Type at least 2 characters to search"}
    />
  );
}


export default function Step1PatientInfo({ formData, onChange, lockedPractice, fieldErrors = {}, onDobBlur, dobInputRef }) {
  const { t } = useTranslation('patientInfo');
  const { t: tCommon } = useTranslation('common');
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [practicesError, setPracticesError] = useState(null);
  
  // Get translated gender options
  const genders = t('genderOptions', { returnObjects: true });

  // Load practices on component mount
  useEffect(() => {
    // If practice is locked, skip fetching and ensure formData is set
    if (lockedPractice) {
      setPractices([{ practice_name: lockedPractice }]);
      setPracticesError(null);
      setPracticesLoading(false);
      if (formData.practice !== lockedPractice) {
        onChange({ practice: lockedPractice });
      }
      return;
    }
    const loadPractices = async () => {
      try {
        setPracticesLoading(true);
        const activePractices = await getActivePractices();
        setPractices(activePractices);
        setPracticesError(null);
      } catch (error) {
        console.error('Failed to load practices:', error);
        setPracticesError('Failed to load practices');
        // Fallback to hardcoded practices if API fails
        setPractices([
          { practice_name: 'STARS Plastic Surgery' },
          { practice_name: 'San Antonio General Surgery' }
        ]);
      } finally {
        setPracticesLoading(false);
      }
    };

    loadPractices();
  }, [lockedPractice]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {lockedPractice ? (
          <TextField
            required
            label={t('labels.practice')}
            fullWidth
            value={lockedPractice}
            InputProps={{ 
              readOnly: true,
              style: { fontWeight: 'bold' }
            }}
            disabled
            helperText={t('helpers.practiceSelected', { practice: lockedPractice })}
          />
        ) : (
          <TextField
            select
            required
            label={t('labels.practice')}
            fullWidth
            value={formData.practice || ''}
            onChange={e => onChange({ practice: e.target.value })}
            disabled={practicesLoading}
            error={!!practicesError || !!fieldErrors.practice}
            helperText={
              fieldErrors.practice || 
              practicesError || 
              (practicesLoading ? t('helpers.loadingPractices') : 
                (formData.practice ? t('helpers.practiceSelected', { practice: formData.practice }) : t('helpers.selectPractice')))
            }
            InputProps={{
              style: formData.practice ? { fontWeight: 'bold' } : {}
            }}
          >
            {practicesLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('helpers.loadingPractices')}
              </MenuItem>
            ) : (
              practices.map(practice => (
                <MenuItem key={practice.practice_id || practice.practice_name} value={practice.practice_name}>
                  {practice.practice_name}
                </MenuItem>
              ))
            )}
          </TextField>
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          label={t('labels.fullName')}
          fullWidth
          value={formData.name || ''}
          onChange={e => onChange({ name: e.target.value })}
          error={!!fieldErrors.name}
          helperText={fieldErrors.name || ''}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.dateOfBirth')}
          type="date"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          inputRef={dobInputRef}
          inputProps={{ 
            max: new Date().toISOString().split('T')[0],
            min: new Date(new Date().setFullYear(new Date().getFullYear() - 105)).toISOString().split('T')[0]
          }}
          value={formData.date_of_birth || ''}
          onChange={e => onChange({ date_of_birth: e.target.value })}
          onBlur={onDobBlur}
          error={!!fieldErrors.date_of_birth}
          helperText={fieldErrors.date_of_birth || ''}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={genders}
          value={formData.gender || null}
          onChange={(e, newValue) => onChange({ gender: newValue })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Gender"
              required
              fullWidth
              error={!!fieldErrors.gender || params.inputProps['aria-invalid']}
              helperText={fieldErrors.gender || ''}
            />
          )}
          disableClearable
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.cellPhone')}
          fullWidth
          required
          value={
            // Format phone number as ###-###-#### if it has enough digits
            formData.cell_phone ? 
              formData.cell_phone
                .replace(/^(\d{3})(\d{0,3})(\d{0,4})$/, (match, p1, p2, p3) => {
                  let formatted = p1;
                  if (p2) formatted += '-' + p2;
                  if (p3) formatted += '-' + p3;
                  return formatted;
                })
              : ''
          }
          onChange={e => {
            // Only allow digits for storage
            const cleaned = e.target.value.replace(/\D/g, '');
            onChange({ cell_phone: cleaned });
          }}
          inputProps={{ 
            maxLength: 12, // Increased to account for the two hyphens
            inputMode: 'tel'
          }}
          error={!!fieldErrors.cell_phone || (formData.cell_phone && formData.cell_phone.length !== 10)}
          helperText={fieldErrors.cell_phone || (formData.cell_phone && formData.cell_phone.length !== 10 ? t('helpers.validPhone') : '')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          label={t('labels.email')}
          type="email"
          fullWidth
          value={formData.email || ''}
          onChange={e => onChange({ email: e.target.value })}
          error={!!fieldErrors.email || (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))}
          helperText={fieldErrors.email || (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) 
            ? t('helpers.validEmail') 
            : '')}
        />
        </Grid>
      <Grid item xs={12}>
        <TextField
          label={t('labels.streetAddress')}
          fullWidth
          value={formData.street_address || ''}
          onChange={e => onChange({ street_address: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.city')}
          fullWidth
          value={formData.city || ''}
          onChange={e => onChange({ city: e.target.value })}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Autocomplete
          options={states}
          value={formData.state || 'TX'}
          onChange={(e, newValue) => onChange({ state: newValue })}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('labels.state')}
              fullWidth
            />
          )}
          size="small"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          label={t('labels.zipCode')}
          fullWidth
          value={formData.zip_code || ''}
          onChange={e => onChange({ zip_code: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <PharmacyAutocomplete
          value={formData.preferred_pharmacy || ''}
          onSelect={pharmacy => onChange({ preferred_pharmacy: pharmacy })}
          error={!!fieldErrors.preferred_pharmacy}
          helperText={fieldErrors.preferred_pharmacy || ''}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={t('labels.howFoundUs')}
          select
          fullWidth
          value={formData.how_found_us || ''}
          onChange={e => onChange({ how_found_us: e.target.value })}
        >
          <MenuItem value="Google/Web Search">{t('howFoundUsOptions.google')}</MenuItem>
          <MenuItem value="Social Media">{t('howFoundUsOptions.socialMedia')}</MenuItem>
          <MenuItem value="Insurance Company Referred">{t('howFoundUsOptions.insuranceReferred')}</MenuItem>
          <MenuItem value="Doctor Office Referred me">{t('howFoundUsOptions.doctorReferred')}</MenuItem>
          <MenuItem value="Friend Referred me">{t('howFoundUsOptions.friendReferred')}</MenuItem>
          <MenuItem value="Other">{t('howFoundUsOptions.other')}</MenuItem>
        </TextField>
      </Grid>
      {['Friend Referred me', 'Doctor Office Referred me', 'Other'].includes(formData.how_found_us) && (
        <Grid item xs={12}>
          <TextField
            label={t('labels.referralSource')}
            fullWidth
            value={formData.referral_source || ''}
            onChange={e => onChange({ referral_source: e.target.value })}
          />
        </Grid>
      )}
    </Grid>
  );
}
